import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import Logo from "../components/Logo";
import PremiumReport from "../components/PremiumReport";
import { getPremiumContentFor } from "../utils/premiumContent";
import { AlertTriangle } from "lucide-react";
import CryptoJS from "crypto-js";

const PremiumSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [premiumData, setPremiumData] = useState<any>(null);
  const [usersName, setUsersName] = useState<string>("");
  // Get the encrypted string (from URL param or localStorage)

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        let decryptedData;
        if (searchParams.has("encrypted")) {
          const encrypted = searchParams.get("encrypted");
          const secretKey =
            import.meta.env.VITE_SECRET_KEY || "your-strong-secret-key"; // Store securely

          if (!encrypted) {
            throw new Error("Encrypted parameter is missing");
          }

          const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
          decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
          console.log("Decrypted data:", decryptedData);
        }

        const customer_email =
          decryptedData.recipientEmail || searchParams.get("customer_email");

        if (!customer_email) {
          throw new Error("No customer email provided");
        }

        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("*")
          .eq("email", customer_email)
          .single();

        if (userError) throw userError;

        setUsersName(userData.first_name + " " + userData.last_name || "");

        // Get the latest test result for this user
        const { data: testResult, error: testError } = await supabase
          .from("test_results")
          .select("*")
          .eq("user_id", userData.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .single();

        if (testError) throw testError;

        // Update user's premium status
        const { error: updateError } = await supabase
          .from("users")
          .update({
            has_premium: true,
            premium_purchased_at: new Date().toISOString(),
            premium_report_data: {
              archetype: testResult.primary_archetype,
              content: getPremiumContentFor(testResult.primary_archetype),
            },
          })
          .eq("id", userData.id);

        if (updateError) throw updateError;

        const content = getPremiumContentFor(testResult.primary_archetype);
        console.log("User primary_archetype:", testResult.primary_archetype);
        console.log("Premium content loaded:", content);
        setPremiumData({
          archetype: testResult.primary_archetype,
          content,
        });
      } catch (err) {
        console.error("Premium verification error:", err);
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando su compra...</p>
        </div>
      </div>
    );
  }

  if (error || !premiumData) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {error || "Unable to load premium content"}
          </h2>
          <p className="text-gray-600 mb-6">
            Para obtener ayuda, contacte con el equipo de soporte en support@latinocareertest.com. Incluya su correo electrónico de confirmación de pago al contactar con el equipo de soporte.
          </p>
          <a
            href="/"
            className="inline-block bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition-colors"
          >
            Regresar a la página de inicio
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-orange-50 py-8">
        <div
          className="container mx-auto px-4 max-w-4xl"
          id="premium-success-page"
        >
          <header className="flex justify-center mb-8">
            <Logo size="medium" />
          </header>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              ¡Felicidades!
            </h1>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              El Reporte Premium de {usersName} está listo
            </h1>
            <p className="text-gray-600">
              Gracias por desbloquear tu <b>Reporte Premium completo del Test Latino™</b>
            </p>
          </div>

          <PremiumReport
            archetype={premiumData.archetype}
            content={premiumData.content}
            usersName={usersName}
          />
        </div>
      </div>
    </>
  );
};

export default PremiumSuccessPage;
